import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { useLang } from "../i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { FileSpreadsheet, FileDown, Loader2, Lock } from "lucide-react";

const PRESETS = {
  essentiel: [
    "code_anonyme", "code_comite", "statut_dossier", "date_soumission", "nom", "prenom", "courriel",
    "faculte", "programme", "cycle", "inscription", "heures_travail_semaine", "revenus_mensuels",
    "reste_scolarite", "exemption_frais", "bourse_accordee", "versement_effectue",
  ],
  comite: [
    "code_comite", "cycle", "situation_familiale", "enfants_charge", "exemption_frais", "heures_travail_semaine",
    "revenus_mensuels", "reste_scolarite", "loyer_pension", "epicerie", "transport_commun", "lettre_presentation",
  ],
};

const storageKey = (mode) => `step_export_cols_${mode}`;

export function ExportDialog({ open, onOpenChange, mode, onExport, exporting }) {
  const { t } = useLang();
  const [catalog, setCatalog] = useState({ sections: [], columns: [] });
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    api.get(`/admin/export/columns?mode=${mode}`).then((r) => {
      setCatalog(r.data);
      const saved = localStorage.getItem(storageKey(mode));
      const available = r.data.columns.filter((c) => !c.locked).map((c) => c.key);
      const initial = saved ? JSON.parse(saved).filter((k) => available.includes(k)) : available;
      setSelected(new Set(initial.length ? initial : available));
    }).catch(() => {});
  }, [open, mode]);

  const available = useMemo(() => catalog.columns.filter((c) => !c.locked), [catalog]);

  const apply = (keys) => {
    const next = new Set(keys.filter((k) => available.some((c) => c.key === k)));
    setSelected(next);
    localStorage.setItem(storageKey(mode), JSON.stringify([...next]));
  };
  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    apply([...next]);
  };
  const toggleSection = (sec, on) => {
    const keys = available.filter((c) => c.section === sec).map((c) => c.key);
    const next = new Set(selected);
    keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
    apply([...next]);
  };

  const run = (fmt) => {
    const ordered = catalog.columns.filter((c) => selected.has(c.key)).map((c) => c.key);
    onExport(fmt, ordered);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            {mode === "anonymous" ? t("export.titleAnon") : t("export.titleFull")}
          </DialogTitle>
          <DialogDescription>{mode === "anonymous" ? t("export.descAnon") : t("export.descFull")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold text-slate-600">{t("export.presets")} :</span>
          <Button size="sm" variant="outline" onClick={() => apply(available.map((c) => c.key))} data-testid="export-preset-all">{t("export.all")}</Button>
          <Button size="sm" variant="outline" onClick={() => apply([])} data-testid="export-preset-none">{t("export.none")}</Button>
          <Button size="sm" variant="outline" onClick={() => apply(PRESETS.essentiel)} data-testid="export-preset-essential">{t("export.essential")}</Button>
          {mode === "anonymous" && (
            <Button size="sm" variant="outline" onClick={() => apply(PRESETS.comite)} data-testid="export-preset-committee">{t("export.committee")}</Button>
          )}
          <span className="ml-auto text-slate-500" data-testid="export-selected-count">{selected.size} / {available.length} {t("export.selected")}</span>
        </div>

        <div className="space-y-4 mt-2">
          {catalog.sections.map((sec) => {
            const cols = catalog.columns.filter((c) => c.section === sec.key);
            if (!cols.length) return null;
            const unlocked = cols.filter((c) => !c.locked);
            const allOn = unlocked.length > 0 && unlocked.every((c) => selected.has(c.key));
            return (
              <div key={sec.key} className="rounded-xl border border-slate-200 p-3" data-testid={`export-section-${sec.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-slate-700">{sec.label}</h4>
                  {unlocked.length > 0 && (
                    <button type="button" onClick={() => toggleSection(sec.key, !allOn)} data-testid={`export-section-toggle-${sec.key}`}
                      className="text-xs font-semibold text-primary hover:underline">
                      {allOn ? t("export.none") : t("export.all")}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5">
                  {cols.map((c) => (
                    <label key={c.key} className={`flex items-center gap-2 text-sm ${c.locked ? "text-slate-400" : "text-slate-700 cursor-pointer"}`}>
                      <Checkbox checked={!c.locked && selected.has(c.key)} disabled={c.locked}
                        onCheckedChange={() => toggle(c.key)} data-testid={`export-col-${c.key}`} />
                      <span className="truncate">{c.label}</span>
                      {c.locked && <Lock className="w-3 h-3 shrink-0" title={t("export.locked")} />}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="export-cancel-btn">{t("admin.archive.cancel")}</Button>
          <Button variant="outline" disabled={!!exporting || selected.size === 0} onClick={() => run("csv")} data-testid="export-run-csv">
            <FileDown className="w-4 h-4 mr-2" /> {t("admin.export.csv")}
          </Button>
          <Button disabled={!!exporting || selected.size === 0} onClick={() => run("xlsx")} data-testid="export-run-xlsx" className="bg-primary hover:bg-primary/90">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            {t("admin.export.excel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
